/// <amd-module name="TFS/NetPromoterSurvey/SurveyComponent" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { Button } from "VSSUI/Button";

import { IRecommender } from "Recommenders/Recommender";
import { DataKeys, IResponse } from "TFS/NetPromoterSurvey/Common";
import { StepByStepSurveyDialog } from "TFS/NetPromoterSurvey/Components/StepByStepSurveyDialog";
import * as NetPromoterSurveyResources from "TFS/NetPromoterSurvey/Resources";

/**
 * Constants used when building the telemetry events
 */
enum TelemetryConstants {
    AreaName = "Survey",
    SubmitFeature = "User.Submit",
    PostponeFeature = "User.Postpone",
    AppearFeature = "Dialog.Appear",
    CloseFeature = "Dialog.Close"
}

export interface ISurveyComponentProps {
    onDismiss: () => void;

    /* Recommender used for logging recommendations and interactions */
    recommender: IRecommender;

    /* Information about the current context */
    isAuthenticated: boolean;
    productName: string;
    selectedHubGroupId: string;
    selectedHubId: string;

    /* Feature flags */
    useNewBranding: boolean;
    verticalNavigationEnabled: boolean;
    verticalBrandingEnabled: boolean;

    /* Cached interaction data */
    data: { [key: string]: string };
}

interface ISurveyComponentState {
    showSurvey: boolean;
    response: IResponse;
    hasScoreBeenSubmitted: boolean;
}

export class SurveyComponent extends VssComponent<IVssComponentProperties, ISurveyComponentState> {
    private _telemetryService: IVssTelemetryService;

    private dialogWrapper: any;

    constructor(props: any, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            showSurvey: false,
            response: {
                role: this.props.data[DataKeys.UserRole]
            },
            hasScoreBeenSubmitted: false
        };

        this._setServices();
    }

    public componentDidMount() {
        if (this._selectedHubIsValid()) {
            this.setState(
                {
                    showSurvey: true
                },
                () => {
                    this._appear();
                }
            );
        }
    }

    public render(): JSX.Element {
        if (!this.state.showSurvey) {
            return <div />;
        }

        return (
            <div id="nps-dialog" className={format("nps-dialog nps-dialog-{0}", "step-by-step")} ref={this._setWrapperRef}>
                <Button
                    subtle={true}
                    iconProps={{ iconName: "Cancel" }}
                    className="nps-dialog-close"
                    ariaLabel={NetPromoterSurveyResources.CloseButtonTitle}
                    onClick={() => {
                        this._postpone(this.state.response, "close");
                    }}
                />
                <StepByStepSurveyDialog
                    productName={this.props.productName}
                    response={this.state.response}
                    isAuthenticated={this.props.isAuthenticated}
                    change={this._handleChange}
                    submit={this._submit}
                    postpone={this._postpone}
                    close={this._close}
                />
            </div>
        );
    }

    private _handleChange = (partialResponse: Partial<IResponse>) => {
        this.setState({ response: { ...this.state.response, ...partialResponse } });
    };

    /* Loggers */

    /**
     * Performs appear action for the current survey context
     */
    private _appear = (): void => {
        this.publishTelemetry(TelemetryConstants.AppearFeature, {});
    };

    /**
     * Performs close action for the current survey context
     *  - Publish telemetry
     *  - Close dialog
     *  - Remove handlers
     */
    private _close = (): void => {
        this.setState({ showSurvey: false }, () => {
            this.publishTelemetry(TelemetryConstants.CloseFeature, {});
            this.props.onDismiss();
        });
    };

    /**
     * Performs submit action for the current survey context
     */
    private _submit = (response: IResponse, exitMethod: string): void => {
        const interactionType = "submit";
        const deferredTimes = 0;
        const telemetryData = this._buildTelemetryData(response, interactionType, deferredTimes, exitMethod, true);

        this.props.recommender.logInteraction(this._buildInteractionData(interactionType, deferredTimes));
        this.publishTelemetry(TelemetryConstants.SubmitFeature, telemetryData);

        this.setState({ hasScoreBeenSubmitted: true });
    };

    /**
     * Performs postpone action for the current survey context
     */
    private _postpone = (response: IResponse, exitMethod: string): void => {
        this._close();

        if (!this.state.hasScoreBeenSubmitted) {
            let interactionType = "postpone";
            let deferredTimes: number = (Number(this.props.data[DataKeys.DeferredTimes]) || 0) + 1;
            if (deferredTimes >= Number(this.props.data[DataKeys.MaxAllowableDeferTimes])) {
                interactionType = "submit";
                deferredTimes = 0;
            }
            this.props.recommender.logInteraction(this._buildInteractionData(interactionType, deferredTimes));
            const telemetryData = this._buildTelemetryData(response, interactionType, deferredTimes, exitMethod, false);
            this.publishTelemetry(TelemetryConstants.PostponeFeature, telemetryData);
        }
    };

    /**
     * Builds core interaction data to be used by telemetry and interation loggin in the recommendation service
     */
    private _buildCoreData = (interactionType: string, deferredTimes: number, userRole?: string): { [key: string]: any } => {
        const data: { [key: string]: any } = {};
        data[DataKeys.UserRole] = userRole;
        data[DataKeys.InteractionType] = interactionType;
        data[DataKeys.DeferredTimes] = deferredTimes;
        return data;
    };

    /**
     * Builds interaction-specific data to be stored in the recommendation service
     */
    private _buildInteractionData = (interactionType: string, deferredTimes: number): { [key: string]: any } => {
        return this._buildCoreData(interactionType, deferredTimes, this.state.response.role);
    };

    /**
     * Builds telemetry data for current survey response
     */
    private _buildTelemetryData = (
        response: IResponse,
        interactionType: string,
        deferredTimes: number,
        exitMethod: string,
        withUserResponse: boolean
    ): { [key: string]: any } => {
        const data: { [key: string]: any } = {};
        data[DataKeys.Interaction] = this._buildCoreData(interactionType, deferredTimes, response.role);
        data[DataKeys.ExitMethod] = exitMethod;
        data[DataKeys.Response] = withUserResponse ? response : undefined;
        return data;
    };

    /**
     * Builds core teleemtry data common to any type of rsponse
     * Contains information about the survey context
     */
    private _buildCoreTelemetryData = () => {
        const core: { [key: string]: any } = {};
        core[DataKeys.SurveyId] = "NetPromoterSurvey";
        core[DataKeys.Url] = window.location.href;
        core[DataKeys.OperationDate] = new Date().toISOString();
        core[DataKeys.FeatureAvailability] = {};
        core[DataKeys.FeatureAvailability]["StepByStepSurveyEnabled"] = true; // Even though at the moment we always set this to true, we still need to include it for back-compat with the telemetry data schema.
        core[DataKeys.FeatureAvailability]["UseNewBranding"] = Boolean(this.props.useNewBranding);
        core[DataKeys.FeatureAvailability]["VerticalNavigationEnabled"] = Boolean(this.props.verticalNavigationEnabled);
        core[DataKeys.FeatureAvailability]["VerticalBranding"] = Boolean(this.props.verticalBrandingEnabled);
        core[DataKeys.AnonymousAccess] = !this.props.isAuthenticated;
        core[DataKeys.HubGroupId] = this.props.selectedHubGroupId;
        core[DataKeys.HubId] = this.props.selectedHubId;
        core[DataKeys.ProductName] = this.props.productName;
        core[DataKeys.PromptId] = this.props.data[DataKeys.PromptId];
        return core;
    };

    /**
     * Publishes telemetry event
     */
    private publishTelemetry = (feature: string, properties: { [key: string]: string }): void => {
        const core = this._buildCoreTelemetryData();
        core[DataKeys.NPSDebugCookieEnabled] = this.props.data[DataKeys.NPSDebugCookieEnabled];
        this._telemetryService.publishEvent(TelemetryConstants.AreaName, feature, { ...core, ...properties });
    };

    /**
     * Set the wrapper ref
     */
    private _setWrapperRef = (node: any) => {
        this.dialogWrapper = node;
    };

    private _setServices = () => {
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    };

    private _selectedHubIsValid(): boolean {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();
        const selectedHubGroupId = (displayedNavigation.length > 2 && displayedNavigation[2]) || "N/A";
        const selectedHubId = (displayedNavigation.length > 3 && displayedNavigation[3]) || "N/A";
        return selectedHubGroupId === this.props.selectedHubGroupId && selectedHubId === this.props.selectedHubId;
    }
}
